import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import API, { BUSINESS_ACCESS_EVENT, BUSINESS_NAME_EVENT, POSTING_ACCESS_EVENT, POSTING_NAME_EVENT, TOKEN_EVENT, TOKEN_KEY, USER_DATA_EVENT, USER_DATA_KEY } from "./API";
import { useLocation } from 'react-router-dom'
import { TokenData, UserLogin } from "@tempu/components/Classes";
import { UserBusinessAccess, UserPostingAccess } from "./Classes";
import moment from "moment";

var TitleContext = createContext<{
	setTitle: (title: string, subtitle?: string)=>void,
	setAction: (element: JSX.Element)=>void,
	title: string,
	subtitle: string,
	action: JSX.Element,
	suffix: string,
}>({
	setTitle: (v: string, s: string)=>null,
	setAction: ()=>null,
	title: null,
	subtitle: null,
	action: null,
	suffix: null,
});
var useTitle = () => useContext(TitleContext);

interface TitleProps extends PropsWithChildren{
	default?: string,
	suffix?: string,
}

var TitleProvider = (props: TitleProps) => {
	var [titleHistory, setTitleHistory] = useState<{ pathname: string, title: string, subtitle?: string }[]>([]);
	var [actionHistory, setActionHistory] = useState<{ pathname: string, element: JSX.Element }[]>([]);
	var location = useLocation();
	var suffix = props.suffix || 'Ebentu';

	var title = titleHistory.find(a=>a.pathname===location.pathname);
	var action = actionHistory.find(a=>a.pathname===location.pathname);

	useEffect(()=>{
		if(!title || !title.title){
			document.title = suffix;
		}else{
			document.title = `${title.title} - ${suffix}`;
		}
	}, [location, titleHistory]);

	var changeTitle = (title: string, subtitle?: string)=>{
		if(!location.key) return;
		var hs = [...titleHistory];
		hs = hs.filter(a=>a.pathname!==location.pathname);
		hs.push({ pathname: location.pathname, title, subtitle });
		setTitleHistory(hs);
	}

	var setAction = (element: JSX.Element)=>{
		if(!location.key) return;
		var hs = [...actionHistory];
		hs = hs.filter(a=>a.pathname!==location.pathname);
		hs.push({ pathname: location.pathname, element });
		setActionHistory(hs);
	}

	return (
		<TitleContext.Provider value={{
			setTitle: changeTitle,
			setAction,
			title: (title?.title || props.suffix),
			subtitle: (title?.subtitle || title?.title || props.suffix),
			action: action?.element,
			suffix,
		}}>
			{props.children}
		</TitleContext.Provider>
	)
}

const UserAuthContext = createContext<{
	logged_in: boolean,
	user: UserLogin["user"],
	logout: (redirect?: boolean)=>void,
	refresh: ()=>void,
}>({
	logged_in: false,
	user: null,
	logout: (redirect?: boolean)=>null,
	refresh: ()=>null,
});
var useUser = () => useContext(UserAuthContext);

var UserProvider = (props: PropsWithChildren)=>{
	var [user, setUser] = useState<UserLogin>(JSON.parse(localStorage.getItem('user_data') || null));
	var [token, setToken] = useState<TokenData>(JSON.parse(localStorage.getItem('admin_token') || null))
	var now = moment().unix();
	var is_expired = !token || (token.expires<=(now+30));

	var updateData = ()=>{
		try{
			var d = JSON.parse(localStorage.getItem(USER_DATA_KEY));
			setUser(d);
			return d as UserLogin;
		}catch(e){}
	}

	var updateToken = ()=>{
		try{
			var d = JSON.parse(localStorage.getItem(TOKEN_KEY));
			setToken(d);
			return d as TokenData;
		}catch(e){}
	}

	var refresh = ()=>{
		updateToken();
		updateData();
		API.getLogin(true);
	}

	var logout = (redirect: boolean=true)=>{
		API.logout();
		if(redirect){
			setTimeout(()=>{
				window.location.href = API.getAuthUrl();
			}, 10);
		}
	}

	useEffect(()=>{
		refresh();
		window.addEventListener(USER_DATA_EVENT, updateData);
		window.addEventListener(TOKEN_EVENT, updateToken);
		return ()=>{
			window.removeEventListener(USER_DATA_EVENT, updateData);
			window.removeEventListener(TOKEN_EVENT, updateToken);
		}
	}, [])
	
	return <UserAuthContext.Provider value={{
		logged_in: !is_expired && !!user,
		user: is_expired ? null : user?.user,
		logout,
		refresh,
	}}>
		{props.children}
	</UserAuthContext.Provider>
}

var hasUserAccess = (a: number | number[], user_access: { admin: boolean, access: number[] }, use_or: boolean, ignore_admin: boolean=false)=>{
	if(!user_access) return false;
	if(user_access.admin && !ignore_admin) return true;
	if(Array.isArray(a)){
		var acc = (a as number[]), valid = [];
		for(var i of acc){
			var ha = user_access.access.indexOf(i);
			if(ha!=-1){
				if(use_or) return true; // USE_OR ONLY NEEDS 1 VALID ACCESS
				valid.push(i);
			}
		}
		if(valid.length>=a.length) return true // HAS ENOUGH ACCESS
	}else{
		if(user_access && user_access.access.indexOf(a)!=-1){
			return true;
		}
	}
}

interface BusinessData{
	business_id: number,
	business_name: string,
}

var BusinessContext = createContext<{
	getBusiness: (business_id: number)=>Promise<BusinessData>,
	getBusinessAccess: (business_id: number)=>Promise<UserBusinessAccess>,
	accessList: UserBusinessAccess[],
	businessList: BusinessData[],
}>({
	getBusiness: ()=>null,
	getBusinessAccess: ()=>null,
	accessList: [],
	businessList: [],
});

var useBusiness = (business_id: number)=>{
	var [loading, setLoading] = useState<boolean>(false);
	var [business, setBusiness] = useState<BusinessData>(null);
	var { getBusiness, accessList, businessList } = useContext(BusinessContext);
	var access = accessList.find(a=>a.business_id===business_id);

	useEffect(()=>{
		if(businessList){
			var bsn = businessList.find(a=>a.business_id===business_id);
			if(bsn){
				return setBusiness(bsn);
			}
		}
		// TODO: Se esta haciendo el query a business/info 2 veces, intentar arreglarlo
		setLoading(true);
		getBusiness(business_id).then(res=>{
			if(res) setBusiness(res);
		}).catch(err=>{
			setBusiness(null);
		}).finally(()=>{
			setLoading(false);
		});
	}, []);

	var hasAccess = (a: number | number[], use_or: boolean=false, ignore_admin: boolean=false)=>{
		if(!access) return false;
		return hasUserAccess(a, access, use_or, ignore_admin);
	}

	return {
		loading,
		business_id: business?.business_id,
		business_name: business?.business_name,
		hasAccess,
		access: (access?.access || []),
		admin: (access?.admin || false),
	}
}

var BusinessProvider = (props: PropsWithChildren)=>{
	var [businessList, setBusinessList] = useState<(BusinessData & { found: boolean })[]>([]);
	var [businessAccess, setBusinessAccess] = useState<UserBusinessAccess[]>([]);

	var getBusiness = async (business_id: number)=>{
		var bn = businessList.find(a=>a.business_id===business_id);
		if(bn){
			if(bn.found) return bn;
			else return null;
		}

		try{
			var res = await API.getBusinessInfo(business_id);
			if(res.error) return null;
			var bl = [...businessList];
			var new_business = {
				found: !res.error && !!res.data,
				business_id: business_id,
				business_name: res.data?.business_name,
			}
			bl.push(new_business);
			setBusinessList(bl);
			return new_business;
		}catch(e){
			return null;
		}
	}

	var getBusinessAccess = async (business_id: number)=>{
		var ba = businessAccess.find(a=>a.business_id===business_id);
		if(ba) return ba;
		try{
			var res = await API.getBusinessAccess(business_id);
			return res.data;
		}catch(e){
			throw e;
		}
	}

	var onBusinessListEvent = (e: { detail: BusinessData[] })=>{
		if(e.detail && e.detail.length>0){
			var bl = [...businessList];
			for(var i of e.detail){
				var ix = bl.findIndex(a=>a.business_id==i.business_id);
				if(ix===-1){
					bl.push({ found: true, business_id: i.business_id, business_name: i.business_name });
				}else{
					bl[ix].found = true;
					bl[ix].business_name = i.business_name;
				}
			}
			setBusinessList(bl);
		}
	}

	var onBusinessAccessEvent = (e: { detail: UserBusinessAccess })=>{
		if(e.detail){
			var ba = [...businessAccess];
			var bix = ba.findIndex(a=>a.business_id===e.detail.business_id);
			if(bix===-1){
				ba.push(e.detail);
			}else{
				ba[bix].admin = e.detail.admin;
				ba[bix].access = e.detail.access;
			}
			setBusinessAccess(ba);
		}
	}
	
	useEffect(()=>{
		window.addEventListener(BUSINESS_NAME_EVENT, (onBusinessListEvent as any), false);
		return ()=>{
			window.removeEventListener(BUSINESS_NAME_EVENT, (onBusinessListEvent as any));
		}
	}, [businessList]);
	
	useEffect(()=>{
		window.addEventListener(BUSINESS_ACCESS_EVENT, (onBusinessAccessEvent as any), false);
		return ()=>{
			window.removeEventListener(BUSINESS_ACCESS_EVENT, (onBusinessAccessEvent as any));
		}
	}, [businessAccess]);

	return <BusinessContext.Provider value={{
		getBusiness,
		getBusinessAccess,
		accessList: businessAccess,
		businessList: businessList,
	}}>
		{props.children}
	</BusinessContext.Provider>
}

interface PostingData{
	posting_id: number,
	posting_name: string,
	pending_users: number,
}

var PostingContext = createContext<{
	getPosting: (posting_id: number, load_external?: boolean)=>Promise<PostingData>,
	getPostingAccess: (posting_id: number)=>Promise<UserPostingAccess>,
	setPostingPendingUsers: (posting_id: number, users: number)=>void,
	postingList: (PostingData & { found: boolean })[],
	accessList: UserPostingAccess[],
}>({
	getPosting: ()=>null,
	getPostingAccess: ()=>null,
	setPostingPendingUsers: ()=>null,
	postingList: [],
	accessList: [],
});

var usePosting = (posting_id: number, load: boolean=true)=>{
	var { getPosting, postingList, accessList, setPostingPendingUsers } = useContext(PostingContext);
	var [loading, setLoading] = useState<boolean>(false);
	var [posting, setPosting] = useState<PostingData>(null);
	var access = accessList.find(a=>a.posting_id===posting_id);

	useEffect(()=>{
		setLoading(true);
		getPosting(posting_id, load).then(res=>{
			setPosting(res);
		}).catch(()=>{
			setPosting(null);
		}).finally(()=>{
			setLoading(false);
		});
	}, []);

	useEffect(()=>{
		if(!postingList) return;
		var pst = postingList.find(a=>a.posting_id==parseInt((posting_id as any)));
		if(pst){
			return setPosting(pst.found ? pst : null);
		}
	}, [postingList]);

	var hasAccess = (a: number | number[], use_or: boolean=false, ignore_admin: boolean=false)=>{
		if(!access) return false;
		return hasUserAccess(a, access, use_or, ignore_admin);
	}

	var setPendingUsers = (users: number)=>{
		if(posting){
			setPosting({
				...posting,
				pending_users: users,
			});
		}
		setPostingPendingUsers(posting_id, users);
	}

	return {
		loading,
		posting_id: posting?.posting_id,
		posting_name: posting?.posting_name,
		pending_users: posting?.pending_users || 0,
		admin: access?.admin || false,
		access: access?.access || [],
		setPendingUsers,
		hasAccess
	}
}

// TODO: Add event on posting list or any other posting router that might get the name of a posting
var PostingProvider = (props: PropsWithChildren)=>{
	var [postingList, setPostingList] = useState<(PostingData & { found: boolean })[]>([]);
	var [accessList, setAccessList] = useState<UserPostingAccess[]>([]);

	var getPostingAccess = async (posting_id: number)=>{
		var pa = accessList.find(a=>a.posting_id===posting_id);
		if(pa) return pa;
		try{
			var res = await API.getPostingAccess(posting_id);
			return res.data;
		}catch(e){
			throw e;
		}
	}

	var onPostingListEvent = (e: { detail: PostingData[] })=>{
		if(e.detail && e.detail.length>0){
			var pl = [...postingList];
			for(var i of e.detail){
				var ix = pl.findIndex(a=>a.posting_id==i.posting_id);
				if(ix===-1){
					pl.push({ found: true, posting_id: i.posting_id, posting_name: i.posting_name, pending_users: 0 });
				}else{
					pl[ix].found = true;
					pl[ix].posting_name = i.posting_name;
				}
			}
			setPostingList(pl);
		}
	}

	var onPostingAccessEvevnt = (e: { detail: UserPostingAccess })=>{
		if(e.detail){
			var pa = [...accessList];
			var bix = pa.findIndex(a=>a.posting_id===e.detail.posting_id);
			if(bix===-1){
				pa.push(e.detail);
			}else{
				pa[bix].admin = e.detail.admin;
				pa[bix].access = e.detail.access;
			}
			setAccessList(pa);
		}
	}

	var getPosting = async (posting_id: number, load_external: boolean=true)=>{
		var pt = postingList.find(a=>a.posting_id===posting_id);
		if(pt){
			if(pt.found) return pt;
			else return null;
		}
		if(load_external){
			try{
				var res = await API.getPostingInfo(posting_id);
				if(res.error) return null;
				var new_posting = {
					found: !!res.data,
					posting_id: posting_id,
					posting_name: res.data?.posting_name,
					pending_users: res.data?.pending_users || 0,
				}
				var pl = [...postingList];
				pl.push(new_posting);
				setPostingList(pl);
				return new_posting;
			}catch(e){
				return null;
			}
		}else{
			return null;
		}
	}

	var setPostingPendingUsers = (posting_id: number, users: number)=>{
		var px = postingList.findIndex(a=>a.posting_id===posting_id);
		if(px===-1) return;
		var npl = [...postingList];
		npl[px].pending_users = users;
		setPostingList(npl);
	}

	useEffect(()=>{
		window.addEventListener(POSTING_NAME_EVENT, (onPostingListEvent as any), false);
		return ()=>{
			window.removeEventListener(POSTING_NAME_EVENT, (onPostingListEvent as any));
		}
	}, [postingList]);
	
	useEffect(()=>{
		window.addEventListener(POSTING_ACCESS_EVENT, (onPostingAccessEvevnt as any), false);
		return ()=>{
			window.removeEventListener(POSTING_ACCESS_EVENT, (onPostingAccessEvevnt as any));
		}
	}, [accessList]);

	return <PostingContext.Provider value={{
		getPosting,
		getPostingAccess,
		setPostingPendingUsers,
		postingList,
		accessList,
	}}>
		{props.children}
	</PostingContext.Provider>
}

export {
	BusinessProvider,
	useBusiness,

	TitleProvider,
	useTitle,

	UserProvider,
	useUser,

	usePosting,
	PostingProvider,
}